import {
  Button,
  Badge,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@predoc/cow';
import { formatDisplayDate } from '@/utils/date';
import {
  SquareCheckBig,
  Download,
  RefreshCw,
  Loader2,
  CircleEllipsis,
  CircleAlert,
} from 'lucide-react';
import {
  type DownloadListItem,
  DownloadItemStatus,
  DownloadItemType,
} from './types';
import {
  useGetDownloadItemLink,
  useSubmitDownloadRegenerateRequest,
} from './hooks/downloadsAPI';
import { triggerDownload } from '@/utils/downloadUtils';

type DownloadButtonProps = {
  documentId: string;
  patientId: string;
  itemType: DownloadItemType;
};

const DownloadButton = ({
  documentId,
  patientId,
  itemType,
}: DownloadButtonProps) => {
  const { refetch, isFetching } = useGetDownloadItemLink(
    documentId,
    patientId,
    itemType,
  );

  const handleDownload = async () => {
    const result = await refetch();
    if (result.data?.signedUrl) {
      triggerDownload(result.data.signedUrl);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5"
      aria-label="Download file"
      onClick={handleDownload}
      disabled={isFetching}
    >
      {isFetching ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Download className="h-4 w-4" />
      )}
      Download
    </Button>
  );
};

type RegenerateButtonProps = {
  documentId: string;
  patientId: string;
  itemType: DownloadItemType;
};

const RegenerateButton = ({
  documentId,
  patientId,
  itemType,
}: RegenerateButtonProps) => {
  const { mutate, isPending } = useSubmitDownloadRegenerateRequest();

  const handleRegenerate = () => {
    mutate({ documentId, patientId, itemType });
  };

  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1.5"
      aria-label="Regenerate file"
      onClick={handleRegenerate}
      disabled={isPending}
    >
      {isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <RefreshCw className="h-4 w-4" />
      )}
      Regenerate
    </Button>
  );
};

type DownloadStatusActionProps = {
  item: DownloadListItem;
};

const DownloadStatusAction = ({ item }: DownloadStatusActionProps) => {
  switch (item.status) {
    case DownloadItemStatus.READY:
      return (
        <DownloadButton
          documentId={item.id}
          patientId={item.patientId}
          itemType={item.itemType}
        />
      );
    case DownloadItemStatus.STALE:
      return (
        <RegenerateButton
          documentId={item.id}
          patientId={item.patientId}
          itemType={item.itemType}
        />
      );
    case DownloadItemStatus.GENERATING:
      return (
        <Badge
          icon={<Loader2 className="text-foreground animate-spin" />}
          className="bg-muted text-foreground"
        >
          Generating
        </Badge>
      );
    case DownloadItemStatus.QUEUED:
      return (
        <Badge
          icon={<CircleEllipsis className="text-foreground" />}
          className="bg-muted text-foreground"
        >
          Queued
        </Badge>
      );
    case DownloadItemStatus.FAILED:
      return (
        <Badge
          icon={<CircleAlert className="text-destructive-foreground" />}
          className="bg-destructive text-destructive-foreground"
        >
          Error
        </Badge>
      );
  }
};

type DownloadItemProps = {
  item: DownloadListItem;
  onMarkAsRead?: (
    documentId: string,
    patientId: string,
    itemType: DownloadItemType,
  ) => void;
};

export const DownloadItem = ({ item, onMarkAsRead }: DownloadItemProps) => {
  const formattedDate =
    formatDisplayDate(item.createdAt, "MM/dd/yyyy '•' h:mma") ?? '';

  const handleMarkAsRead = () => {
    onMarkAsRead?.(item.id, item.patientId, item.itemType);
  };

  return (
    <div
      className="flex items-start gap-3 border-b px-4 py-3"
      role="listitem"
      aria-label={`${item.title}, ${item.status}`}
    >
      <div className="relative mt-1.5 shrink-0">
        {item.isUnread && (
          <span className="bg-destructive block h-2.5 w-2.5 rounded-full" />
        )}
        {!item.isUnread && <span className="block h-2.5 w-2.5" />}
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-sm leading-snug font-semibold">{item.title}</p>
        <p className="text-muted-foreground mt-0.5 text-xs">{formattedDate}</p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {item.isUnread && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleMarkAsRead}
                aria-label="Mark as read"
              >
                <SquareCheckBig className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" hideArrow>
              Mark as read
            </TooltipContent>
          </Tooltip>
        )}
        <DownloadStatusAction item={item} />
      </div>
    </div>
  );
};
